import React, { useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css'; // Core Swiper styles
import { Pagination, EffectFade, Navigation, Autoplay } from 'swiper/modules';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';

function BannerOne() {
    const swiperRef = useRef(null);
    const [heroSlides, setHeroSlides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch hero slides directly
    useEffect(() => {
        const fetchHeroSlides = async () => {
            try {
                setLoading(true);
                console.log('BannerOne: Fetching hero slides...');
                const response = await apiService.getHeroSlides();
                console.log('BannerOne: Hero slides response:', response);
                
                if (response && response.results) {
                    setHeroSlides(response.results);
                    console.log('BannerOne: Loaded', response.results.length, 'hero slides');
                } else {
                    setHeroSlides([]);
                    console.warn('BannerOne: No hero slides in response');
                }
            } catch (err) {
                console.error('BannerOne: Error fetching hero slides:', err);
                setError(err.message);
                setHeroSlides([]);
            } finally {
                setLoading(false);
            }
        };

        fetchHeroSlides();
    }, []);

    useEffect(() => {
        // Function to add animation classes
        const animationProperties = () => {
            const slides = document.querySelectorAll('.hero-inner');
            const activeSlide = document.querySelector('.swiper-slide-active .hero-inner');
            
            // Remove animation classes from all slides
            slides.forEach(slide => {
                const elements = slide.querySelectorAll('[data-ani]');
                elements.forEach(element => {
                    element.classList.remove('th-animated');
                });
            });

            // Add animation classes to active slide
            if (activeSlide) {
                const elements = activeSlide.querySelectorAll('[data-ani]');
                elements.forEach(element => {
                    const animationType = element.getAttribute('data-ani');
                    const delay = element.getAttribute('data-ani-delay') || '0.1s';
                    
                    element.style.animationDelay = delay;
                    element.classList.add('th-animated', animationType);
                });
            }
        };

        const swiperInstance = swiperRef.current?.swiper;
        if (swiperInstance) {
            // Initial animation
            setTimeout(animationProperties, 100);
            
            // Animation on slide change
            swiperInstance.on('slideChange', animationProperties);
            
            return () => {
                swiperInstance.off('slideChange', animationProperties);
            };
        }
    }, [heroSlides]); // Re-run when heroSlides change

    // Show loading state
    if (loading) {
        return (
            <section className="th-hero-wrapper hero-1" id="hero" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="container">
                    <div className="text-center">
                        <h2>Loading Hero Section...</h2>
                        <div className="spinner-border text-primary mt-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // Show error state
    if (error) {
        return (
            <section className="th-hero-wrapper hero-1" id="hero" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="container">
                    <div className="text-center">
                        <h2>Welcome to Tourm</h2>
                        <p>Your Adventure Awaits</p>
                        <Link to="/tours" className="th-btn style3">Explore Tours</Link>
                    </div>
                </div>
            </section>
        );
    }

    // Show slides
    return (
        <section className="th-hero-wrapper hero-1" id="hero">
            <Swiper
                ref={swiperRef}
                modules={[Pagination, EffectFade, Navigation, Autoplay]}
                spaceBetween={0}
                slidesPerView={1}
                loop={heroSlides.length > 2} // Only enable loop if we have more than 2 slides
                effect="fade"
                speed={1000}
                pagination={{
                    clickable: true,
                    el: '.hero-slider-pagination'
                }}
                navigation={{
                    nextEl: '.hero-slider-next',
                    prevEl: '.hero-slider-prev'
                }}
                autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                }}
                className="th-slider hero-slider-1"
            >
                {heroSlides.length > 0 ? heroSlides.map((slide, index) => (
                    <SwiperSlide key={slide.id || index}>
                        <div className="hero-inner">
                            <div className="th-hero-bg" style={{ 
                                backgroundImage: slide.background_image ? `url(${slide.background_image})` : 'url(/assets/img/hero/hero_bg_1_1.jpg)' 
                            }}>
                                <img src={slide.background_image || '/assets/img/hero/hero_bg_1_1.jpg'} alt="Hero Background" />
                            </div>
                            <div className="container">
                                <div className="hero-style1">
                                    <span className="sub-title" data-ani="slideinup" data-ani-delay="0.2s">
                                        {slide.subtitle || 'Explore World'}
                                    </span>
                                    <h1 className="hero-title">
                                        <span className="title1" data-ani="slideinup" data-ani-delay="0.4s">
                                            {slide.title || 'Adventure Awaits'}
                                        </span>
                                    </h1>
                                    <p className="hero-text" data-ani="slideinup" data-ani-delay="0.6s">
                                        {slide.description || 'Discover amazing places and create unforgettable memories.'}
                                    </p>
                                    <div className="btn-group" data-ani="slideinup" data-ani-delay="0.8s">
                                        <Link to={slide.primary_button_link || '/tours'} className="th-btn style3">
                                            {slide.primary_button_text || 'Book Now'}
                                        </Link>
                                        <Link to={slide.secondary_button_link || "/about"} className="th-btn style2">
                                            {slide.secondary_button_text || "Learn More"}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>
                )) : (
                    // Fallback slide if no slides available
                    <SwiperSlide>
                        <div className="hero-inner">
                            <div className="th-hero-bg" style={{ backgroundImage: 'url(/assets/img/hero/hero_bg_1_1.jpg)' }}>
                                <img src="/assets/img/hero/hero_bg_1_1.jpg" alt="Hero Background" />
                            </div>
                            <div className="container">
                                <div className="hero-style1">
                                    <span className="sub-title" data-ani="slideinup" data-ani-delay="0.2s">
                                        Explore World
                                    </span>
                                    <h1 className="hero-title">
                                        <span className="title1" data-ani="slideinup" data-ani-delay="0.4s">
                                            Welcome to Tourm
                                        </span>
                                    </h1>
                                    <p className="hero-text" data-ani="slideinup" data-ani-delay="0.6s">
                                        Discover amazing places and create unforgettable memories with our expertly crafted tours.
                                    </p>
                                    <div className="btn-group" data-ani="slideinup" data-ani-delay="0.8s">
                                        <Link to="/tours" className="th-btn style3">Book Now</Link>
                                        <Link to="/about" className="th-btn style2">Learn More</Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>
                )}
                
                {/* Navigation */}
                <button className="slider-arrow hero-slider-prev">
                    <i className="far fa-arrow-left"></i>
                </button>
                <button className="slider-arrow hero-slider-next">
                    <i className="far fa-arrow-right"></i>
                </button>
                
                {/* Pagination */}
                <div className="hero-slider-pagination"></div>
            </Swiper>
        </section>
    );
}

export default BannerOne
